/**
 * HOME RUN — the pinned pair's hand-over driver (Kyle, 2026-08-11).
 *
 * "I want the images — when I scroll in, the image to be sticky, but the text on
 * the right to scroll. And where it would make sense I want the next image to
 * fade in and the text can continue to scroll." Then, the same day: "map the
 * text to the same animation so that it's centered vertically with the image,
 * and as I scroll it transitions to the next section of text just like the
 * image does."
 *
 * Then, the same day: "make the transitions not scroll-dependent, so animated
 * — probably 1/2 second fades."
 *
 * Does exactly ONE thing: measures how far the pinned stack has travelled
 * through its track, works out WHICH pair that makes active, and marks the
 * elements that should be on screen. Every pair's hold is the same length —
 * the runway divided by the pair count — and the stylesheet is what decides how
 * long that is. See measure(). Nothing else. The pinning is CSS, the
 * centring is CSS, and both fades are CSS transitions whose duration and curve
 * are tokens. This file has no design value in it at all, which is the point:
 * dialing the beat is a token edit, never a JS edit.
 *
 * ⚠ IT DOES NOT ANIMATE ANYTHING, AND IT USED TO. Until 2026-08-11 it wrote a
 * continuous share per element every frame and the stylesheet clamped an
 * opacity out of it, so the dissolve advanced exactly as fast as the reader
 * turned the wheel — a flick snapped it, a slow drag smeared it across half a
 * screen, and stopping mid-window parked the beat between two photographs. Now
 * scroll decides only which pair is active and the transition plays at its own
 * pace. --eg-hr-sp, --eg-hr-sp-next, --eg-hr-fade-start and --eg-hr-fade-end
 * are all deleted.
 *
 * ── WHAT IT MEASURES ───────────────────────────────────────────────────────
 *
 *   travel = track.height - stack.height        the pinned scroll budget
 *   p      = (stack.top - track.top) / travel   0 as the pin takes hold,
 *                                               1 as it releases
 *
 * BOTH BOXES, AGAINST EACH OTHER, AND NO CONSTANTS. Before the pin the stack's
 * border box starts exactly at the track's, so the numerator is 0; while pinned
 * the stack's top holds still and the track's runs away underneath it; at
 * release the stack's bottom meets the track's, so the numerator equals the
 * denominator. That exactness is why section-eg-home-run.css gives the track no
 * padding — a padded track would offset the numerator at both ends and leave a
 * slice of the beat unreachable.
 *
 * IT IS NOT MEASURED OFF THE VIEWPORT. The previous version divided by
 * window.innerHeight, because the copies were in flow and each one's arrival
 * WAS the thing to time. Once the copy pins too there is no flow position left
 * to read — all three copies have the same rect and it stops moving the moment
 * the stack sticks.
 *
 * ── WHAT IT WRITES ─────────────────────────────────────────────────────────
 *
 *   data-eg-hr-shown  on the elements that should be on screen, removed from
 *                     the rest. That is the whole output — no numbers, no
 *                     inline styles, nothing per-frame that the compositor has
 *                     to keep up with.
 *
 *                     PICTURES 0…active are marked, not just the active one.
 *                     They share a cell and paint in DOM order, so everything
 *                     below the active picture is hidden behind it; marking
 *                     them keeps the frame opaque all the way down and lets the
 *                     top one dissolve AWAY on the way back up, revealing the
 *                     one beneath rather than the page. Only the active COPY is
 *                     marked, because type cannot stack.
 *
 *   data-eg-hr-armed  on the track, one animation frame after the first commit,
 *                     which is what switches the transitions on. The opening
 *                     state is painted instantly and only genuine hand-overs
 *                     animate; without it a reader reloading part-way down the
 *                     page would watch everything fade from its committed state
 *                     to its correct one.
 *
 * THE PAIR COUNT COMES FROM THE DOM, and the slots are derived from it and from
 * measured geometry — see measure(). Adding a fourth pair to the Liquid
 * re-slices the runway on its own, with no edit here; the only number that
 * would change is the pair count in the track's block-size.
 *
 * THE FIRST PICTURE IS NEVER FADED and this file does not know that. It marks
 * the first picture like any other; the CSS simply gives it no opacity rule
 * (`.eg-homerun__media ~ .eg-homerun__media` selects the second and third
 * only), because the first is the base layer the other two dissolve over. That
 * is what stops the frame ever being empty, and it means a failure here costs
 * the hand-over, never the photograph.
 *
 * IT IS REVERSIBLE, DELIBERATELY. Every value is recomputed from live geometry
 * on every frame, so scrolling back up dissolves the pictures back in the order
 * they arrived. There is no one-shot latch here — unlike the arrival fades in
 * eg-scroll-sweep.js, which are on a clock and must not re-run.
 *
 * MOTION PREFERENCE IS NOT CHECKED HERE, ON PURPOSE. Marking an element is not
 * motion — the `transition` that turns a mark into a fade lives entirely in the
 * section's CSS, inside `@media (prefers-reduced-motion: no-preference)`, along
 * with the pinning and the opacity. That media query is the single arbiter,
 * including when the reader flips the OS setting mid-session, which a JS-side
 * check would miss without a listener nobody would remember to add.
 *
 * MOBILE IS NOT GATED HERE EITHER. The marks are written at every width and the
 * phone's CSS reads none of them: below 749px the stack is one column in DOM
 * order, nothing is pinned, and no rule anywhere gives an opacity or a
 * transition to a marked element. A media query in here would be a second
 * breakpoint to keep in step with the stylesheet's.
 *
 * WHY NOT eg-scroll-sweep.js. It is the shared driver for eg-home-panel and
 * eg-home-builtline and it was the first thing considered. `mount()` will not
 * write a number until `split()` has succeeded, and `split()` replaces each
 * line's text with one <span> per character — pointing it at this beat would
 * shred three headings and eight paragraphs into some 1,400 spans to obtain a
 * scroll position. Its product is a per-character index; what this beat needs
 * is one mark per photograph. Nothing was duplicated by not using it: the
 * rAF coalescing and the passive listeners below are the shape every driver in
 * this theme has, and there is no third implementation of the sweep.
 *
 * WHY THIS FILE EXISTS RATHER THAN `animation-timeline: view()`. The native
 * feature does all of this with no script. It is not in release Firefox, and
 * Kyle uses Firefox — measured on his own build, 2026-08-04, Firefox 153.0.1:
 * CSS.supports('animation-timeline', 'scroll()') === false and 'ScrollTimeline'
 * in window === false. Same call, and the same reasoning, as eg-home-sisters.js.
 *
 * FAIL-VISIBLE, AND IT IS THE WHOLE SAFETY STORY. `data-eg-hr-ready` is set
 * only after the stack and at least one photograph have been found. Both live
 * CSS rules require that attribute, so if this script never loads, throws, or
 * finds nothing, the section renders in its BASE composition — each photograph
 * beside its own copy, in three ordinary rows, every picture fully opaque —
 * which is the same thing a reduced-motion reader gets and is exactly what
 * shipped before this pass. There is no state of this page in which a
 * photograph is invisible and stays that way.
 */
(function () {
  var track = document.querySelector('[data-eg-hr-track]');
  if (!track) return;

  var stack = track.querySelector('[data-eg-hr-stack]');
  if (!stack) return;

  var stills = stack.querySelectorAll('.eg-homerun__media');
  if (!stills.length) return;

  /* The copy block each picture answers to, resolved ONCE. `nextElementSibling`
     is the markup's own contract — picture, then the copy it belongs to — and
     re-querying it on every frame would be a DOM walk per picture per frame for
     an answer that cannot change.

     A picture with no copy after it is skipped rather than fatal. */
  var pairs = [];
  for (var i = 0; i < stills.length; i += 1) {
    var copy = stills[i].nextElementSibling;
    if (copy) pairs.push({ still: stills[i], copy: copy });
  }
  if (!pairs.length) return;

  /* HOW FAR THE PIN HAS TRAVELLED, IN PIXELS.

     Before the pin the stack's border box starts exactly at the track's, so
     this is 0; while pinned the stack's top holds still and the track's runs
     away underneath it; at release the stack's bottom meets the track's. That
     exactness is why section-eg-home-run.css gives the track no padding — a
     padded track would offset it at both ends and leave a slice of the beat
     unreachable.

     IT IS PIXELS, NOT A 0…1 FRACTION, because the slots are no longer equal
     lengths and a fraction would have to be un-normalised against them again
     immediately. `edges` is in the same units, so resolve() is a comparison.

     BOTH GUARDS RETURN THE END OF THE RUNWAY, not the start, which is the
     fail-visible direction — the last pair shown, never a blank frame:

       no viewport   window.innerHeight is 0 in a few hosting situations (a
                     background tab in some engines, a zero-sized iframe), so
                     there is nothing on screen to animate.
       travel <= 0   the track is not taller than the pin, which is every
                     fallback state — no explicit track height on a phone or
                     under reduced motion. */
  function travelled() {
    var t = track.getBoundingClientRect();
    var s = stack.getBoundingClientRect();
    var travel = t.height - s.height;
    if (!window.innerHeight || travel <= 0) return Infinity;
    var d = s.top - t.top;
    return d < 0 ? 0 : d > travel ? travel : d;
  }

  /* ── THE SLOTS: THE RUNWAY, DIVIDED EQUALLY ───────────────────────────────
     (Kyle, 2026-08-11: "each item needs to be visible for the amount of
     time/space it would be if it were scrolling normally".)

     EVERY PAIR HOLDS FOR THE SAME DISTANCE, so this is one division. The
     stylesheet has already sized the track as the pin times (1 + pairs ×
     --eg-hr-pair-dwell), so the travel it leaves is exactly `pairs` slots and
     dividing it back out recovers them. The dwell is never read here — it is
     baked into the track height, and reading it as well would be two copies of
     one number free to disagree.

     ⚠ WHAT THIS REPLACED. An earlier build measured the beat's scroll-IN and
     scroll-OUT and subtracted them from the first and last pair's slots, so
     that each pair's TOTAL time on screen came out equal. It made the practice
     pair's hold 220px and Kyle's verdict was "that is completely wrong… see how
     immediately the last directional item scrolls?". Entry and exit are the
     beat arriving and departing — what every section on this page does — not
     dwell owed to whichever pair happens to be visible during them. The
     measurement of the sticky offset, the entry and exit terms and the derived
     share are all deleted with that model; none of it has a reader left.

     --eg-hr-switch-band IS THE ONLY TOKEN STILL READ, and it is unitless for
     the reason a length could not be: a custom property's computed value is not
     resolved to pixels, so `33px` would arrive as a string for this code to
     guess the unit of. A fraction of a slot it has already measured needs no
     conversion. A missing or unparseable value degrades to 0 — a bare
     threshold, correct but twitchy on a boundary — never to NaN.

     RE-MEASURED ON RESIZE, NOT PER FRAME. getComputedStyle forces style
     resolution and commit() runs on every scroll frame; neither the slot nor
     the band can change without a resize. */
  var edges = [];
  var bandPx = 0;

  function measure() {
    var t = track.getBoundingClientRect();
    var s = stack.getBoundingClientRect();
    var slot = Math.max((t.height - s.height) / pairs.length, 0);

    var band = parseFloat(
      window.getComputedStyle(track).getPropertyValue('--eg-hr-switch-band'),
    );
    bandPx = band > 0 ? band * slot : 0;

    /* The pinned distance at which each hand-over falls. One fewer than the
       pairs: the last pair holds to the end of the runway and the pin releases
       under it. */
    edges = [];
    for (var i = 1; i < pairs.length; i += 1) edges.push(slot * i);
  }

  /* HOW FAR THE PIN HAS TRAVELLED, IN PIXELS.

     Before the pin the stack's border box starts exactly at the track's, so
     this is 0; while pinned the stack's top holds still and the track's runs
     away underneath it; at release the stack's bottom meets the track's. That
     exactness is why section-eg-home-run.css gives the track no padding — a
     padded track would offset it at both ends and leave a slice of the beat
     unreachable.

     IT IS PIXELS, NOT A 0…1 FRACTION, because the slots are no longer equal
     lengths and a fraction would have to be un-normalised against them again
     immediately. `edges` is in the same units, so resolve() is a comparison.

     BOTH GUARDS RETURN THE END OF THE RUNWAY, not the start, which is the
     fail-visible direction — the last pair shown, never a blank frame:

       no viewport   window.innerHeight is 0 in a few hosting situations (a
                     background tab in some engines, a zero-sized iframe), so
                     there is nothing on screen to animate.
       travel <= 0   the track is not taller than the pin, which is every
                     fallback state — no explicit track height on a phone or
                     under reduced motion. */
  /* rAF-coalesced. `scroll` fires far more often than the compositor paints,
     and every handler here ends in a style write — writing more than once per
     frame is pure waste, and on a trackpad it is a lot of waste. */
  var queued = false;

  /* THE ACTIVE PAIR, WITH A DEAD-BAND AROUND EVERY BOUNDARY.

     `edges` holds the pinned distance at which each hand-over falls, in px, so
     this is a walk along a number line rather than arithmetic on slot indices —
     which is what lets the slots be different lengths.

     A BARE THRESHOLD WOULD BE CORRECT AND UNUSABLE. A reader parked on a
     boundary, a trackpad settling, or a phone's momentum oscillating one pixel
     flips the active pair back and forth and retriggers the whole half-second
     fade every time. So the two directions disagree on purpose: forward, the
     switch happens --eg-hr-switch-band PAST the edge; backward, the same
     distance BEFORE it. Between those two points nothing changes whichever way
     the reader is drifting.

     ⚠ IT BIASES THE ENDS BY ITS OWN WIDTH, which is measured rather than
     ignored: scrolling down, every edge is crossed late, so the first pair
     gains a band and the last loses one — 1112/1080/1048 against a flat 1080,
     three per cent, in the direction nobody notices.

     BOTH LOOPS RUN TO EXHAUSTION, WHICH IS WHAT HANDLES A FAST FLICK. Crossing
     three boundaries in one frame advances the index three times inside this
     call and the caller writes the result ONCE — the browser never resolves
     style for an intermediate pair, so there is nothing to queue and nothing to
     play in sequence. Reversing is the mirror image. */
  var active = 0;

  function resolve(d) {
    while (active < edges.length && d > edges[active] + bandPx) active += 1;
    while (active > 0 && d < edges[active - 1] - bandPx) active -= 1;
    return active;
  }

  function commit() {
    queued = false;

    var current = resolve(travelled());

    /* PICTURES 0…current ARE MARKED, NOT JUST `current`. They are stacked in one
       cell and paint in DOM order, so everything below the active one is hidden
       behind it — marking them keeps the frame opaque all the way down and lets
       the top one dissolve AWAY on the way back up, revealing the one beneath
       rather than the page. Only the active COPY is marked: type cannot stack.

       Attributes rather than inline styles, because the animating is the
       stylesheet's job now. Setting an attribute that is already set is a no-op
       in every engine, so an unchanged frame costs nothing. */
    for (var i = 0; i < pairs.length; i += 1) {
      if (i <= current) pairs[i].still.setAttribute('data-eg-hr-shown', '');
      else pairs[i].still.removeAttribute('data-eg-hr-shown');

      if (i === current) pairs[i].copy.setAttribute('data-eg-hr-shown', '');
      else pairs[i].copy.removeAttribute('data-eg-hr-shown');
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(commit);
  }

  /* `passive` because this never calls preventDefault — without it the browser
     has to wait for the handler before it can scroll, which is exactly the jank
     this whole file is trying not to introduce. */
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener(
    'resize',
    function () {
      measure();
      schedule();
    },
    { passive: true },
  );

  /* ⚠ THE ATTRIBUTE GOES ON FIRST AND THE ORDER IS NOT INTERCHANGEABLE. The
     track has NO explicit height until `[data-eg-hr-ready]` matches, so a
     commit() taken before this line measures a track no taller than its pin,
     hits the `travel <= 0` guard and writes the end state onto every pair — the
     last copy visible and the other two at zero. The next animation frame would
     correct it, but the reader would have seen the wrong pair land first.

     Setting it here and measuring immediately afterwards is safe rather than
     racy: `getBoundingClientRect()` inside commit() forces a synchronous layout,
     so it reads the track's NEW height, and no paint can happen between two
     statements in the same turn. There is no frame in which the committed
     defaults are on screen. */
  track.setAttribute('data-eg-hr-ready', '');
  measure();
  commit();

  /* ARM THE TRANSITIONS ONE FRAME LATE, AND ONLY ONCE.

     The opening state is painted, THEN the fades are switched on. Without this
     a reader who reloads part-way down the page would watch every element
     transition from its committed state to its correct one — three copies
     fading out of a pile and a photograph dissolving in, half a second of
     nonsense before the beat even starts. Arming on the next animation frame
     means the first paint is instant and only genuine hand-overs animate.

     The attribute is never removed, so this cannot re-fire; and because
     `transition` lives entirely on [data-eg-hr-armed] rules inside the motion
     media query, a reduced-motion reader never gets one at all. */
  requestAnimationFrame(function () {
    track.setAttribute('data-eg-hr-armed', '');
  });

  /* One more pass once the webfonts have settled. The runway itself is derived
     from tokens and does not move, but a deferred script can run before the
     section's stylesheet has been applied — in which case the first
     measurement is of an unstyled track. Commissioner and Mezcal also decide
     how tall the copy blocks are, which is what the fallback layout's height
     depends on. It re-reads the tail as well, for the same reason — an unapplied
     stylesheet has no --eg-hr-switch-band to read. `document.fonts.ready` is the
     cheapest "layout has settled" signal available without a ResizeObserver,
     and it costs one extra pass on a promise that has usually already resolved.
     Same belt-and-braces as eg-home-sisters.js. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      measure();
      schedule();
    });
  }
})();
